/**
 * NovoCredenciamentoPage — geração do Edital de Chamamento Público de
 * Credenciamento (fase Edital).
 *
 * Réplica do new_credenciamento.html: só existe para processo cujo DFD
 * marcou a hipótese de credenciamento (art. 74, IV); a fundamentação é a
 * PLURALIDADE (art. 79), nunca exclusividade. Processo comum é desviado
 * para o Edital competitivo.
 */

import {
  faArrowLeft,
  faExclamationTriangle,
  faGavel,
  faIdCard,
  faScroll,
  faSpinner,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import {
  gerarCredenciamento,
  novoCredenciamento,
  type ParametrosCredenciamento,
} from '../../api/credenciamento'
import { ApiError } from '../../api/http'
import { useToast } from '../../components/Toast'

const DISTRIBUICOES = [
  ['rodizio', 'Rodízio entre credenciados'],
  ['livre_escolha_beneficiario', 'Livre escolha do beneficiário'],
  ['ordem_demanda', 'Conforme a demanda'],
] as const

const ORIGENS_TABELA = [
  ['tabela_administracao', 'Tabela fixada pela Administração'],
  ['tabela_referencial', 'Tabela referencial oficial'],
  ['preco_mercado', 'Preço de mercado (mercados fluidos)'],
] as const

const PADRAO: ParametrosCredenciamento = {
  numero_edital: '',
  data_inicio_inscricoes: '',
  vigencia_credenciamento: '',
  prazo_analise: '10',
  local_inscricao: '',
  forma_distribuicao_demanda: 'rodizio',
  origem_tabela_precos: 'tabela_administracao',
  referencial_tabela_precos: '',
  outras_exigencias_habilitacao: '',
}

const CLASSE_INPUT =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100'

function Campo({
  rotulo, children, largo = false, ajuda,
}: {
  rotulo: string
  children: React.ReactNode
  largo?: boolean
  ajuda?: string
}) {
  return (
    <label className={`block ${largo ? 'md:col-span-2' : ''}`}>
      <span className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {rotulo}
      </span>
      {children}
      {ajuda && <span className="mt-1 block text-xs text-slate-500">{ajuda}</span>}
    </label>
  )
}

export function NovoCredenciamentoPage() {
  const { id } = useParams()
  const idNumerico = Number(id)
  const navegar = useNavigate()
  const { notificar } = useToast()
  const [parametros, setParametros] =
    useState<ParametrosCredenciamento>(PADRAO)

  const { data, isLoading, error } = useQuery({
    queryKey: ['credenciamento-novo', idNumerico],
    queryFn: () => novoCredenciamento(idNumerico),
    enabled: Number.isFinite(idNumerico),
    retry: false,
  })

  // pré-preenche com o que o DFD já definiu
  useEffect(() => {
    if (!data) return
    setParametros((atual) => ({
      ...atual,
      vigencia_credenciamento: data.vigencia_credenciamento || '',
      forma_distribuicao_demanda: data.forma_distribuicao_demanda || 'rodizio',
      origem_tabela_precos: data.origem_tabela_precos || 'tabela_administracao',
      referencial_tabela_precos: data.referencial_tabela_precos || '',
    }))
  }, [data])

  const gerar = useMutation({
    mutationFn: () => gerarCredenciamento(idNumerico, parametros),
    onSuccess: () => navegar(`/tasks/credenciamento/${idNumerico}`),
    onError: (e: Error) => notificar('error', e.message),
  })

  const definir = (campo: keyof ParametrosCredenciamento, valor: string) =>
    setParametros((atual) => ({ ...atual, [campo]: valor }))

  if (isLoading) {
    return (
      <p className="mt-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Conferindo a cadeia do processo…
      </p>
    )
  }
  if (error || !data) {
    // processo comum: o documento certo é o Edital competitivo
    const detalhes = error instanceof ApiError
      ? (error.detalhes as { edital_competitivo?: boolean } | undefined)
      : undefined
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-dark-card">
        <FontAwesomeIcon
          icon={detalhes?.edital_competitivo ? faScroll : faExclamationTriangle}
          className="mb-4 text-5xl text-amber-400"
        />
        <h1 className="mb-2 text-2xl font-semibold text-slate-700 dark:text-slate-200">
          {detalhes?.edital_competitivo
            ? 'Este processo não é de credenciamento'
            : 'Não é possível iniciar o Chamamento'}
        </h1>
        <p className="mb-6 text-slate-500">
          {(error as Error)?.message
            ?? 'ETP e TR devem ser gerados antes do Edital de Chamamento.'}
        </p>
        <div className="flex justify-center gap-3">
          {detalhes?.edital_competitivo && (
            <Link
              to={`/edital/novo/${idNumerico}`}
              className="inline-flex items-center rounded-lg bg-purple-600 px-6 py-3 font-semibold text-white hover:bg-purple-700"
            >
              <FontAwesomeIcon icon={faScroll} className="mr-2" />
              Ir para o Edital
            </Link>
          )}
          <Link
            to="/processos"
            className="inline-flex items-center rounded-lg bg-slate-600 px-6 py-3 font-semibold text-white hover:bg-slate-700"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Voltar
          </Link>
        </div>
      </div>
    )
  }

  return (
    <form
      className="mx-auto max-w-4xl"
      onSubmit={(e) => {
        e.preventDefault()
        gerar.mutate()
      }}
    >
      <header className="mb-8 text-center">
        <h1 className="mb-2 text-3xl font-bold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faIdCard} className="mr-3 text-teal-600" />
          Gerar Edital de Chamamento — Credenciamento
        </h1>
        <p className="text-slate-600 dark:text-slate-400">{data.objeto}</p>
      </header>

      <section className="mb-6 rounded-xl border border-teal-200 bg-teal-50 p-6 dark:border-teal-800 dark:bg-teal-900/20">
        <h2 className="mb-2 text-lg font-semibold text-teal-800 dark:text-teal-300">
          <FontAwesomeIcon icon={faGavel} className="mr-2" />
          Art. 78, I c/c Art. 79 da Lei 14.133/2021
        </h2>
        <p className="text-sm text-teal-700 dark:text-teal-400">
          Hipótese definida no DFD: <strong>{data.hipotese_descricao}</strong>.
          O chamamento permanece aberto a novos interessados durante toda a
          vigência — contrata-se TODOS os aptos (pluralidade), com contratações
          individuais por inexigibilidade (art. 74, IV).
        </p>
      </section>

      <section className="mb-6 rounded-xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Campo rotulo="Número do Edital de Chamamento *">
            <input type="text" required value={parametros.numero_edital ?? ''} placeholder="Ex.: 001/2026"
              onChange={(e) => definir('numero_edital', e.target.value)} className={CLASSE_INPUT} />
          </Campo>
          <Campo rotulo="Início do recebimento das inscrições *"
            ajuda="O edital permanece aberto a novos interessados durante toda a vigência.">
            <input type="date" required value={parametros.data_inicio_inscricoes ?? ''}
              onChange={(e) => definir('data_inicio_inscricoes', e.target.value)} className={CLASSE_INPUT} />
          </Campo>
          <Campo rotulo="Vigência do credenciamento">
            <input type="text" value={parametros.vigencia_credenciamento ?? ''}
              placeholder="Ex.: 12 (doze) meses, prorrogável"
              onChange={(e) => definir('vigencia_credenciamento', e.target.value)} className={CLASSE_INPUT} />
          </Campo>
          <Campo rotulo="Prazo de análise dos pedidos (dias úteis)">
            <input type="number" min={1} max={60} value={parametros.prazo_analise ?? '10'}
              onChange={(e) => definir('prazo_analise', e.target.value)} className={CLASSE_INPUT} />
          </Campo>
          <Campo rotulo="Local/forma de inscrição" largo>
            <input type="text" value={parametros.local_inscricao ?? ''}
              placeholder="Ex.: no Setor de Licitações da Prefeitura ou pelo e-mail licitacao@municipio.gov.br"
              onChange={(e) => definir('local_inscricao', e.target.value)} className={CLASSE_INPUT} />
          </Campo>
          <Campo rotulo="Distribuição da demanda">
            <select value={parametros.forma_distribuicao_demanda}
              onChange={(e) => definir('forma_distribuicao_demanda', e.target.value)} className={CLASSE_INPUT}>
              {DISTRIBUICOES.map(([valor, rotulo]) => <option key={valor} value={valor}>{rotulo}</option>)}
            </select>
          </Campo>
          <Campo rotulo="Origem da tabela de preços">
            <select value={parametros.origem_tabela_precos}
              onChange={(e) => definir('origem_tabela_precos', e.target.value)} className={CLASSE_INPUT}>
              {ORIGENS_TABELA.map(([valor, rotulo]) => <option key={valor} value={valor}>{rotulo}</option>)}
            </select>
          </Campo>
          {parametros.origem_tabela_precos === 'tabela_referencial' && (
            <Campo rotulo="Tabela referencial" largo>
              <input type="text" value={parametros.referencial_tabela_precos ?? ''}
                placeholder="Ex.: Tabela SUS, SINAPI…"
                onChange={(e) => definir('referencial_tabela_precos', e.target.value)} className={CLASSE_INPUT} />
            </Campo>
          )}
          <Campo rotulo="Outras exigências de habilitação" largo>
            <textarea rows={3} value={parametros.outras_exigencias_habilitacao ?? ''}
              onChange={(e) => definir('outras_exigencias_habilitacao', e.target.value)} className={CLASSE_INPUT} />
          </Campo>
        </div>
      </section>

      {data.tem_credenciamento_gerado && (
        <p className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm font-medium text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
          Já existe um Edital de Chamamento gerado neste processo. Gerar
          novamente substitui o conteúdo atual.
        </p>
      )}

      <div className="flex flex-col justify-between gap-3 pb-4 md:flex-row">
        <Link to="/processos" className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-200 px-6 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-300 dark:bg-dark-hover dark:text-slate-200">
          <FontAwesomeIcon icon={faArrowLeft} />
          Voltar
        </Link>
        <button
          type="submit"
          disabled={gerar.isPending}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-cyan-600 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-teal-700 hover:to-cyan-700 disabled:opacity-60"
        >
          <FontAwesomeIcon icon={gerar.isPending ? faSpinner : faWandMagicSparkles} spin={gerar.isPending} />
          Gerar Chamamento Com LicitIA
        </button>
      </div>
    </form>
  )
}
